import { useRemote } from '../../../Utils/Utils';
import { useEffect, useContext, useMemo, useState } from 'react';
import { applyMethod, analyticsExportFilename } from '../AnalyticsUtils';
import { format } from 'date-fns';
import { BannerContext } from '../../../InternalApiApp';
import AnalyticsCounter from '../../../components/Analytics/AnalyticsCounter';
import FlexWrapper from '../../FlexWrapper';
import PortfolioAnalyticsChart from '../Charts/PortfolioAnalyticsChart';
import LoadingIcon from '../../../components/LoadingIcon';
import AnalyticsHeader from './AnalyticsHeader';
import Tooltip from '../../../components/popups/Tooltip';
import moment from 'moment';


const UsageMetricsPortfolio = ({ timeFrame, withHeader, passedMetrics, url, alternativeName, mb, customeDate }) => {
	const remote = useRemote();
	const [data, setData] = useState([]);
	const [metrics, setMetrics] = useState(null)
	const [rawData, setRowData] = useState(null)
	const [loading, setLoading] = useState(false)
	const { bannerAdditionalInfo } = useContext(BannerContext)

	const monthsDiff = useMemo(() => {
		if (!customeDate?.from || !customeDate?.to) return 1;
		return moment(customeDate.to).diff(moment(customeDate.from), 'months');
	}, [customeDate]);

	useEffect(() => {
		if (customeDate && customeDate.from && customeDate.to) {
			setLoading(true);
			const end = moment(customeDate.to).format('YYYY-MM-DD');
			const start = moment(customeDate.from).format('YYYY-MM-DD');
			const daysDiff = monthsDiff === 0 && moment(customeDate.to).diff(moment(customeDate.from), 'days');
			const products = bannerAdditionalInfo?.platforms.map(platform =>
				platform.products.map(p => p.selected ? `product=${p.id}` : null)
				.filter(e => e !== null)
				.join("&")
			)
			const freq = monthsDiff < 2 ? "daily" : "weekly";

			Promise.all([
				remote.get(`/analytics/aggregates/viewers?frequency=${freq}&from=${start}&to=${end}${products ? "&" + products : ""}`),
				remote.get(`/analytics/aggregates/downloaders?frequency=${freq}&from=${start}&to=${end}${products ? "&" + products : ""}`),
			]).then(([viewersResponse, downloadersResponse]) => {
				const list = [];
				for (var i = 0; i < viewersResponse.length; i++) {
					list.push({
						name: viewersResponse[i].date, // format(new Date(results[i].date), 'dd/MM/yyyy'),
						users: viewersResponse[i].value,
						downloads: downloadersResponse[i].value  // both response should the same length and dates
					})
				}
				setData(list);
				setRowData(viewersResponse)
				setLoading(false);

				//metrics:	
				const myMetrics = {}
				const totals = []

				if (viewersResponse.length > 0) {
					const promiseMetrics = passedMetrics.map(async (m) => {
						if (m.endpoint) {
							const path = m.endpoint.replace("${end}", end).replace("${date}", start).replace("${products}", products ? "&" + products : "")
							const fetchedDocs = await remote.get(path)
							const value = applyMethod(m, fetchedDocs, myMetrics, totals)
							myMetrics[m.label] = value.value
							totals.push(value.total)

						} else {
							const value = applyMethod(m, viewersResponse, myMetrics, totals)
							myMetrics[m.label] = value.value
							totals.push(value.total)
						}
					})

					Promise.all(promiseMetrics).then(() => {
						if (Object.keys(myMetrics).length === passedMetrics.length) {
							const metricsArray = Object.keys(myMetrics).map(m => {
								const passedMetricsObj = passedMetrics.reduce((a, c) => ({ ...a, [c.label]: c }), {})
								return <Tooltip label={m + " over " + (monthsDiff ? monthsDiff + (monthsDiff < 2 ? " Month" : " Months") : daysDiff + (daysDiff < 2 ? " Day" : " Days"))} key={m}>
									<AnalyticsCounter
										key={m}
										label={m}
										count={myMetrics[m]}
										index={passedMetricsObj[m]?.index}
										valueStyle={passedMetricsObj[m]?.valueStyle || null}
										labelStyle={passedMetricsObj[m]?.labelStyle || null}
									/>
								</Tooltip>

							})
							setMetrics(metricsArray.sort((a, b) => a.props.children.props.index - b.props.children.props.index));
						}
					})
				}
			})
		}
	}, [url, bannerAdditionalInfo, timeFrame, passedMetrics, remote, customeDate, monthsDiff])


	const renderTooltip = (payload) => {
		if (!payload || !payload.active || !payload.payload || payload.payload.length === 0) return null;
		let value = payload.payload[0].payload.users;
		let downloadValue = payload.payload[0].payload.downloads;
		let date = new Date(payload.payload[0].payload.name);

		return (
			<div style={{ padding: 10, borderRadius: '0.25rem', boxShadow: '0 1px 5px rgba(0, 0, 0, 0.15)', display: 'flex', gap: '0.5rem', flexDirection: 'column', backgroundColor: '#fff' }}>
				<div type="body" style={{ fontSize: '1rem', color: '#333B5A' }}>{date ? format(date, "do MMMM yyyy") : ""}</div>
				<div type="body" style={{ display: "flex", alignItems: "center", gap: 8, color: "#0E8EFD", fontWeight: 500, fontSize: '1.25rem' }}>
					{/* <div style={{ backgroundColor: "#0f8efd", borderRadius: "50%", width: 12, height: 12 }}></div> */}
					Unique users (viewing): {value}
				</div>
				<div type="body" style={{ display: "flex", alignItems: "center", gap: 8, color: "#62d9b7", fontWeight: 500, fontSize: '1.25rem' }}>
					Unique users (downloading): {downloadValue}
				</div>
			</div>
		);
	}

	const exportHeaders = useMemo(() => [
		{ label: 'Date', key: 'name' },
		{ label: 'Unique Visitors', key: 'users' },
		{ label: 'Unique Downloaders', key: 'downloads' },
	], []);

	const exportFilename = useMemo(
		() => analyticsExportFilename('overall-usage', customeDate),
		[customeDate]
	);

	return (
		<>
			{withHeader &&
				<AnalyticsHeader
					mb={mb ? mb : 'm'}
					exportHeaders={exportHeaders}
					exportData={data}
					exportFilename={exportFilename}
					exportLoading={loading}
				>
					{alternativeName ? alternativeName : "Usage"}
				</AnalyticsHeader>
			}
			{
				rawData?.length === 0
					? <div>
						<p className='colour-text-neutral-dark-50'>No data to display</p>
						<div style={{ height: 170 }}></div>
					</div>
					: (metrics?.length === 0)
						?
						<div style={{ height: 305, display: 'flex' }}>
							<LoadingIcon centered />
						</div>
						: <>
							<FlexWrapper fullWidth gap="xxl">{metrics}</FlexWrapper>
							<PortfolioAnalyticsChart data={data || []} renderTooltip={(payload) => renderTooltip(payload)} tickFormatter={(value) => format(new Date(value), "dd/MM/yyyy")} legendUnit={monthsDiff < 2 ? "Day" : "Week"} />
						</>
			}
		</>
	);
}

export default UsageMetricsPortfolio;